import React, { useEffect, useState } from "react";
import { api, resolveImg } from "../../api";

// Editor for the Home page slideshow.
// Manages an ordered list of image URLs. Upload adds to the list,
// drag reorders, click × removes. Save persists to backend.
const HomeSlidesEditor = () => {
  const [slides, setSlides] = useState(null); // null while loading
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    api.getHomeSlides().then(setSlides).catch(() => setSlides([]));
  }, []);

  const update = (next) => { setSlides(next); setDirty(true); };

  const onUpload = async (files) => {
    setUploading(true);
    try {
      const results = [];
      for (const f of files) {
        const r = await api.upload(f);
        results.push(r.url);
      }
      update([...(slides || []), ...results]);
    } catch (e) {
      alert("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const onRemove = (i) => {
    update(slides.filter((_, k) => k !== i));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.updateHomeSlides(slides || []);
      setDirty(false);
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (target) => {
    if (dragIdx === null || dragIdx === target) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...slides];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(target, 0, moved);
    update(arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  if (slides === null) return <p className="p-8 font-serif text-[color:var(--ink-mute)]">Loading…</p>;

  return (
    <div className="max-w-4xl p-6 md:p-8">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="font-serif text-2xl">Home slideshow</p>
          <p className="font-serif text-[13px] text-[color:var(--ink-mute)] mt-1">
            These are the photos that scroll on the home page. Drag to reorder.
            {slides.length === 0 && " If empty, the site falls back to project & commercial covers."}
          </p>
        </div>
        <label className="font-serif text-[13px] tlink cursor-pointer whitespace-nowrap">
          + Add images
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => { onUpload(Array.from(e.target.files)); e.target.value = ""; }}
            data-testid="home-slides-upload"
          />
        </label>
      </div>

      {uploading && <p className="font-serif text-[12px] text-[color:var(--ink-mute)] mb-3">Uploading…</p>}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {slides.map((url, idx) => (
          <div
            key={`${url}-${idx}`}
            className={`relative group aspect-square bg-[color:var(--paper-2)] overflow-hidden cursor-move ${
              overIdx === idx && dragIdx !== idx ? "ring-2 ring-[color:var(--ink)]" : ""
            } ${dragIdx === idx ? "opacity-40" : ""}`}
            draggable
            onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragIdx(idx); }}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
            onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
            data-testid={`home-slide-${idx}`}
          >
            <img src={resolveImg(url)} alt="" className="w-full h-full object-cover pointer-events-none" />
            <span className="absolute top-1 left-1 bg-white/95 text-[10px] px-1.5 py-0.5 font-serif counter">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="absolute top-1 right-1 bg-white/95 hover:bg-white text-[color:var(--ink)] w-6 h-6 flex items-center justify-center font-serif text-sm opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove"
              data-testid={`home-slide-remove-${idx}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {slides.length === 0 && (
        <p className="font-serif text-[13px] text-[color:var(--ink-mute)] italic mt-4">
          No slides yet — click <em>+ Add images</em> to upload the photos you want on the home page.
        </p>
      )}

      <div className="mt-8 flex items-center gap-3 border-t border-[color:var(--line)] pt-6">
        <button
          onClick={onSave}
          disabled={saving || !dirty}
          className="btn-sober disabled:opacity-40"
          data-testid="home-slides-save"
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
        {dirty && (
          <span className="font-serif text-[12px] text-[color:var(--ink-mute)]">Unsaved changes</span>
        )}
      </div>
    </div>
  );
};

export default HomeSlidesEditor;
